import React, { useEffect, useState, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Star, ArrowRight, Play, Sparkles, Users, Award } from 'lucide-react';
import { mockData } from '../mock';
import * as api from '../services/api';
import SEO from '../components/SEO';
import { generateOrganizationSchema, generatePersonSchema, generateBreadcrumbSchema } from '../utils/schemaGenerator';
import { applyTypographyStyle } from '../utils/applyTypography';
import VideoThumbnail from '../components/VideoThumbnail';

const Home = () => {
  const [isVisible, setIsVisible] = useState({});
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const videoRef = useRef(null);

  const featuredTestimonials = useMemo(() => {
    if (!data?.testimonials) return [];
    const featured = data.testimonials.filter(t => t.featured);
    return (featured.length > 0 ? featured : data.testimonials).slice(0, 3);
  }, [data?.testimonials]);

  const philosophyParagraphs = useMemo(() => {
    const text = data?.lyfeSimply?.description || data?.about?.philosophy || mockData.lyfeSimply?.philosophy || '';
    return text.split('\n').filter(p => p.trim());
  }, [data?.lyfeSimply?.description, data?.about?.philosophy]);

  // Fix React muted attribute bug - set imperatively via ref
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.muted = true;
    }
  }, [data]);

  // Fetch home data from API
  useEffect(() => {
    const fetchHomeData = async () => {
      try {
        setLoading(true);
        const homeData = await api.getHomeData();
        setData(homeData);
        setError(null);
      } catch (err) {
        setError(err.message);
        // Fallback to mock data if API fails
        setData({
          settings: mockData.site,
          about: mockData.about,
          therapies: mockData.services,
          testimonials: mockData.testimonials,
          events: mockData.events,
          reviews: mockData.reviews,
          lyfeSimply: mockData.lyfeSimply
        });
      } finally {
        setLoading(false);
      }
    };

    fetchHomeData();
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible((prev) => ({ ...prev, [entry.target.id]: true }));
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('[data-animate]').forEach((el) => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, [data]);

  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-purple-400 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-white text-lg">Loading...</p>
        </div>
      </div>
    );
  }

  // Don't render if no data
  if (!data) {
    return null;
  }

  // Generate combined structured data
  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [
      generateOrganizationSchema(),
      generatePersonSchema(),
      generateBreadcrumbSchema([
        { name: "Home", url: "https://therapy-booking-cms.preview.emergentagent.com" }
      ])
    ]
  };

  return (
    <div className="min-h-screen overflow-hidden">
      <SEO 
        title="Lyfe Simply - Hypnotherapy & Life Coaching in Dubai"
        description="Transform your life with expert hypnotherapy, life coaching, and professional training in Dubai. 15+ years experience. Book your session today with Jacky."
        keywords="hypnotherapy Dubai, life coach Dubai, hypnotherapy training, clinical hypnotherapy, stress management, anxiety treatment, weight management, smoking cessation, NLP Dubai, Jacky therapist"
        image="https://images.unsplash.com/photo-1506126613408-eca07ce68773?w=1200&h=630&fit=crop"
        url="https://therapy-booking-cms.preview.emergentagent.com"
        structuredData={structuredData}
      />
      
      {/* Hero Section - Premium Elite Therapist Design */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        {/* Background Image with Elegant Gradient Overlay */}
        <div className="absolute inset-0 z-0">
          <img 
            src={data.settings?.hero_image || "https://customer-assets.emergentagent.com/job_lyfeclone/artifacts/n6vfj8y8_liftapp%20%289%29.png"}
            alt="Hero Background"
            className="w-full h-full object-cover object-[70%_30%] md:object-[center_20%]"
            fetchPriority="high"
            loading="eager"
            decoding="async"
            width={1920}
            height={1080}
          />
          {/* Sophisticated gradient overlay - darker on left for text, fades to right */}
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/30"></div>
          {/* Subtle warm overlay for intimacy */}
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/40"></div>
        </div>

        {/* Content - Left aligned for elegance */}
        <div className="container mx-auto px-6 sm:px-12 lg:px-20 relative z-10">
          <div className="max-w-2xl pt-56 pb-16 sm:py-32">
            {/* Elegant accent line */}
            <div className="w-16 h-[2px] bg-gradient-to-r from-[#C6B7D9] to-[#2F8F8B] mb-8"></div>
            
            {/* Headline - Refined typography, preserves line breaks from CMS */}
            <h1 
              className={`text-2xl sm:text-5xl lg:text-6xl font-extralight text-white leading-[1.15] tracking-wide whitespace-pre-line ${
                data.settings?.hero_subtitle ? 'mb-8' : 'mb-10'
              }`}
              style={{
                fontFamily: "'Cormorant Garamond', 'Georgia', serif",
                letterSpacing: '0.02em',
                ...applyTypographyStyle(data.settings?.hero_typography, 'h1')
              }}
            >
              {data.settings?.hero_title || 'Transform Your Life Through Hypnotherapy & Coaching'}
            </h1>
            
            {/* Subtitle - Only show if content exists */}
            {data.settings?.hero_subtitle && (
              <p 
                className="text-lg sm:text-xl text-white/80 mb-10 max-w-xl font-light leading-relaxed tracking-wide whitespace-pre-line"
                style={{
                  fontFamily: "'Cormorant Garamond', 'Georgia', serif",
                  ...applyTypographyStyle(data.settings?.hero_typography, 'body')
                }}
              >
                {data.settings.hero_subtitle}
              </p>
            )}
              
            {/* Buttons - Outline style with color borders */}
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <Link to="/contact">
                <Button 
                  className="w-full sm:w-auto bg-transparent px-7 py-4 sm:py-3 text-sm font-light tracking-wider transition-all duration-500 hover:scale-[1.02] border-2"
                  style={{ 
                    borderColor: '#2F8F8B',
                    color: '#ffffff',
                    borderRadius: '2px',
                    ...applyTypographyStyle(data.settings?.hero_typography, 'button') 
                  }}
                >
                  {data.settings?.hero_button1_text || 'Book Consultation'}
                </Button>
              </Link>
              <Link to="/therapies">
                <Button 
                  className="w-full sm:w-auto bg-transparent px-7 py-4 sm:py-3 text-sm font-light tracking-wider transition-all duration-500 hover:scale-[1.02] border-2"
                  style={{ 
                    borderColor: '#C6B7D9',
                    color: '#ffffff',
                    borderRadius: '2px',
                    ...applyTypographyStyle(data.settings?.hero_typography, 'button') 
                  }}
                >
                  {data.settings?.hero_button2_text || 'Explore Services'}
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* Decorative element - subtle accent */}
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent"></div>
      </section>

      {/* Lyfe Simply Philosophy - Text Left, Video Right */}
      <section 
        id="brand-section" 
        data-animate
        className={`py-12 sm:py-20 relative overflow-hidden transition-all duration-1000 ${
          isVisible['brand-section'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-20'
        }`}
        style={{ backgroundColor: data.settings?.lyfe_simply_bg_color || '#FAF8F4' }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center">
            {/* Left - Text Content */}
            <div className="order-2 lg:order-1">
              <div className="flex items-center mb-4 sm:mb-6">
                <Sparkles style={{ color: '#C9A24D' }} size={28} className="mr-3" />
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-light text-gray-800">
                  {data.lyfeSimply?.title || 'Simplifying Lives'}
                </h2>
              </div>
              <div className="space-y-4">
                {philosophyParagraphs.map((paragraph, idx) => (
                    <p key={`desc-${idx}`} className="text-base sm:text-lg text-gray-700 leading-relaxed font-light">
                      {paragraph}
                    </p>
                  ))
                }
              </div>
            </div>
            
            {/* Right - Video (Portrait Mode) */}
            <div className="order-1 lg:order-2 flex justify-center">
              {data.lyfeSimply?.video_url ? (
                <div className="relative rounded-lg overflow-hidden shadow-xl w-full max-w-[280px] sm:max-w-[320px]" style={{ aspectRatio: '9/16' }}>
                  {data.lyfeSimply.video_url.includes('youtube.com') || data.lyfeSimply.video_url.includes('youtu.be') ? (
                    <iframe
                      src={`${data.lyfeSimply.video_url.replace('watch?v=', 'embed/').replace('youtu.be/', 'youtube.com/embed/')}?autoplay=1`}
                      title="Lyfe Simply Video"
                      className="w-full h-full"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  ) : data.lyfeSimply.video_url.includes('vimeo.com') ? (
                    <iframe
                      src={`${data.lyfeSimply.video_url.replace('vimeo.com/', 'player.vimeo.com/video/')}?autoplay=1`}
                      title="Lyfe Simply Video"
                      className="w-full h-full"
                      frameBorder="0"
                      allow="autoplay; fullscreen; picture-in-picture"
                      allowFullScreen
                    />
                  ) : (
                    <video
                      ref={videoRef}
                      src={data.lyfeSimply.video_url}
                      autoPlay
                      muted
                      playsInline
                      controls
                      className="w-full h-full object-cover"
                    />
                  )}
                </div>
              ) : (
                <div className="relative rounded-lg overflow-hidden shadow-xl w-full max-w-[280px] sm:max-w-[320px] bg-gradient-to-br from-purple-100 to-teal-50 flex items-center justify-center" style={{ aspectRatio: '9/16' }}>
                  <div className="text-center p-8">
                    <Play size={48} className="mx-auto mb-4 text-purple-400" />
                    <p className="text-gray-500 text-sm">Add a video in the CMS</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
      
      {/* Stats Bar - with CMS Background Color */}
      <section className="relative" style={{ backgroundColor: data.settings?.stats_bg_color || '#FAF8F4' }}>
        <div className="grid grid-cols-3 gap-0 border-y border-gray-200 max-w-5xl mx-auto">
          <div className="p-4 sm:p-8 flex flex-col items-center justify-center border-r border-gray-200 hover:bg-white/50 transition-all duration-300 group">
            <Users style={{ color: '#C9A24D' }} className="mb-2 sm:mb-3 group-hover:scale-110 transition-transform" size={24} />
            <div 
              className="text-gray-800 text-xl sm:text-3xl font-light mb-1 sm:mb-2"
              style={applyTypographyStyle(data.settings?.stats_typography, 'h2')}
            >
              {data.settings?.stat1_number || '500+'}
            </div>
            <div 
              className="text-gray-600 text-xs sm:text-sm uppercase tracking-wide text-center"
              style={applyTypographyStyle(data.settings?.stats_typography, 'body')}
            >
              {data.settings?.stat1_label || 'Happy Clients'}
            </div>
          </div>
          <div className="p-4 sm:p-8 flex flex-col items-center justify-center border-r border-gray-200 hover:bg-white/50 transition-all duration-300 group">
            <Award style={{ color: '#C9A24D' }} className="mb-2 sm:mb-3 group-hover:scale-110 transition-transform" size={24} />
            <div 
              className="text-gray-800 text-xl sm:text-3xl font-light mb-1 sm:mb-2"
              style={applyTypographyStyle(data.settings?.stats_typography, 'h2')}
            >
              {data.settings?.stat2_number || '15+'}
            </div>
            <div 
              className="text-gray-600 text-sm uppercase tracking-wide"
              style={applyTypographyStyle(data.settings?.stats_typography, 'body')}
            >
              {data.settings?.stat2_label || 'Years Experience'}
            </div>
          </div>
          <div className="p-4 sm:p-8 flex flex-col items-center justify-center hover:bg-white/50 transition-all duration-300 group">
            <Star style={{ color: '#C9A24D' }} className="mb-2 sm:mb-3 group-hover:scale-110 transition-transform" size={24} />
            <div 
              className="text-gray-800 text-xl sm:text-3xl font-light mb-1 sm:mb-2"
              style={applyTypographyStyle(data.settings?.stats_typography, 'h2')}
            >
              {data.settings?.stat3_number || '5.0'}
            </div>
            <div 
              className="text-gray-600 text-xs sm:text-sm uppercase tracking-wide text-center"
              style={applyTypographyStyle(data.settings?.stats_typography, 'body')}
            >
              {data.settings?.stat3_label || 'Average Rating'}
            </div>
          </div>
        </div>
      </section>

      {/* Who Am I Section - with CMS Background Color */}
      <section 
        id="about-section" 
        data-animate
        className={`py-12 sm:py-20 transition-all duration-1000 ${
          isVisible['about-section'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-20'
        }`}
        style={{ backgroundColor: data.settings?.about_bg_color || '#C6B7D9' }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div className="relative">
              <img 
                src={data.about?.image_url || mockData.about.image} 
                alt="About" 
                className="w-full h-[300px] sm:h-[400px] lg:h-[500px] object-cover rounded-lg shadow-xl"
                loading="lazy"
                decoding="async"
                width={600}
                height={500}
              />
            </div>
            <div>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-light text-gray-800 mb-4 sm:mb-6">
                {data.about?.title || 'Who Am I'}
              </h2>
              <p className="text-gray-700 text-base sm:text-lg leading-relaxed mb-6 sm:mb-8 font-light">
                {data.about?.description || mockData.about.description}
              </p>
              <Link to="/vlogs">
                <Button 
                  className="w-full sm:w-auto text-white px-6 sm:px-8 py-3 rounded-full font-medium hover:scale-105 transition-all shadow-lg"
                  style={{ backgroundColor: '#2F8F8B' }}
                >
                  Learn More <ArrowRight size={18} className="ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section - HIDDEN */}
      {/* Uncomment to show services on homepage
      <section 
        id="services-section" 
        data-animate
        className={`py-16 sm:py-24 lg:py-32 relative overflow-hidden transition-all duration-1000 ${
          isVisible['services-section'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-20'
        }`}
        style={{ backgroundColor: '#FAF8F4' }}
      >
        ... services content ...
      </section>
      */}

      {/* Testimonials Section - with CMS Background Color */}
      <section 
        id="testimonials-section" 
        data-animate
        className={`py-32 text-white relative overflow-hidden transition-all duration-1000 ${
          isVisible['testimonials-section'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-20'
        }`}
        style={{ backgroundColor: data.settings?.testimonials_bg_color || '#C6B7D9' }}
      >
        {/* Subtle pattern overlay */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle, #3A3A3A 1px, transparent 1px)',
            backgroundSize: '50px 50px'
          }}></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 relative z-10">
          <div className="text-center mb-20">
            <h2 className="text-5xl md:text-6xl font-bold mb-6 text-gray-800">{data.settings?.testimonials_title || 'What Our Clients Say'}</h2>
            <div className="w-20 h-1 bg-gradient-to-r from-purple-600 to-teal-600 mx-auto mb-6"></div>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto">
              {data.settings?.testimonials_subtitle || 'Real stories of transformation and healing'}
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {featuredTestimonials.map((testimonial, idx) => (
              <div key={testimonial.id} className="group" style={{ animationDelay: `${idx * 0.1}s` }}>
                <div className="relative">
                  <div className="absolute -inset-1 bg-gradient-to-r from-purple-600 to-teal-600 rounded-2xl blur opacity-25 group-hover:opacity-50 transition-all duration-300"></div>
                  <Card className="relative bg-white/90 backdrop-blur-xl border border-white/20 rounded-2xl overflow-hidden hover:bg-white transition-all duration-300">
                    <div className="relative overflow-hidden group/img" style={{ aspectRatio: '9/16' }}>
                      {testimonial.video_url ? (
                        <VideoThumbnail
                          videoUrl={testimonial.video_url}
                          fallbackImage={testimonial.image_url}
                          alt={testimonial.name}
                          className="w-full h-full"
                          captureTime={0.85}
                        />
                      ) : (
                        <>
                          <img 
                            src={testimonial.image_url} 
                            alt={testimonial.name}
                            className="w-full h-full object-cover group-hover/img:scale-110 transition-transform duration-500"
                            loading="lazy"
                            decoding="async"
                            width={360}
                            height={640}
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                        </>
                      )}
                    </div>
                    
                    <CardContent className="p-4">
                      {/* Only show stars for non-video testimonials */}
                      {!testimonial.video_url && testimonial.rating > 0 && (
                        <div className="flex mb-2">
                          {[...Array(testimonial.rating)].map((_, i) => (
                            <Star key={`star-${i}`} size={14} className="text-yellow-400 fill-current" />
                          ))}
                        </div>
                      )}
                      {/* Only show content if not empty */}
                      {testimonial.content && testimonial.content.trim() && (
                        <p className="text-gray-700 text-sm italic leading-relaxed line-clamp-2">
                          "{testimonial.content}"
                        </p>
                      )}
                      <div className={`pt-3 ${testimonial.content && testimonial.content.trim() ? 'mt-3 border-t border-gray-200' : ''}`}>
                        <div className="font-bold text-gray-800 text-sm">{testimonial.name}</div>
                        {testimonial.role && <div className="text-xs text-gray-600">{testimonial.role}</div>}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            ))}
          </div>
          
          <div className="text-center mt-12">
            <Link to="/testimonials">
              <Button className="bg-white text-purple-600 hover:bg-gray-100 text-lg px-10 py-6 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105">
                View All Testimonials
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Events Section - HIDDEN */}
      {/* Uncomment to show events on homepage
      <section id="events-section">
        ... events content ...
      </section>
      */}

      {/* Google Reviews Section - with CMS Background Color */}
      <section 
        id="reviews-section" 
        data-animate
        className={`py-32 text-white relative overflow-hidden transition-all duration-1000 ${
          isVisible['reviews-section'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-20'
        }`}
        style={{ backgroundColor: data.settings?.reviews_bg_color || '#C6B7D9' }}
      >
        {/* Subtle pattern overlay */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle, #3A3A3A 1px, transparent 1px)',
            backgroundSize: '50px 50px'
          }}></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-5xl md:text-6xl font-bold mb-6 text-gray-800">{data.settings?.reviews_title || 'Google Reviews'}</h2>
            <div className="flex items-center justify-center space-x-3 mb-4">
              <div className="flex">
                {[...Array(5)].map((_, i) => (
                  <Star key={`star-${i}`} size={28} className="text-yellow-400 fill-current" />
                ))}
              </div>
              <span className="text-2xl font-bold text-gray-800">5.0</span>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {data.reviews.map((review, idx) => (
              <div key={review.id} className="group" style={{ animationDelay: `${idx * 0.1}s` }}>
                <div className="relative">
                  <div className="absolute -inset-1 bg-gradient-to-r from-purple-600 to-teal-600 rounded-2xl blur opacity-25 group-hover:opacity-50 transition-all duration-300"></div>
                  <Card className="relative bg-white/90 backdrop-blur-xl border border-white/20 rounded-2xl overflow-hidden hover:bg-white transition-all duration-300">
                    <CardContent className="p-6">
                      <div className="flex mb-3">
                        {[...Array(review.rating)].map((_, i) => (
                          <Star key={`star-${i}`} size={16} className="text-yellow-400 fill-current" />
                        ))}
                      </div>
                      <p className="text-gray-700 mb-4 text-sm leading-relaxed">{review.comment}</p>
                      <div className="pt-3 border-t border-gray-200">
                        <div className="text-sm font-semibold text-gray-800">{review.name}</div>
                        <div className="text-xs text-gray-600">{new Date(review.date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}</div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            ))}
          </div>
          
          <div className="text-center mt-12">
            <Link to="/testimonials">
              <Button className="bg-white text-purple-600 hover:bg-gray-100 text-lg px-10 py-6 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105">
                Read All Reviews
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section - with CMS content */}
      <section 
        className="py-32 text-white relative overflow-hidden"
        style={{ background: data.settings?.cta_bg_color || 'linear-gradient(to bottom right, #7c3aed, #6d28d9, #0d9488)' }}
      >
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle, white 2px, transparent 2px)',
            backgroundSize: '50px 50px'
          }}></div>
        </div>
        
        <div className="max-w-4xl mx-auto px-4 text-center relative z-10">
          <Sparkles className="mx-auto mb-6 text-yellow-300" size={48} />
          <h2 className="text-5xl md:text-6xl font-bold mb-6">{data.settings?.cta_title || 'Ready to Transform Your Life?'}</h2>
          <p className="text-xl md:text-2xl mb-12 text-white/90 leading-relaxed">
            {data.settings?.cta_subtitle || 'Take the first step towards a better you. Book your consultation today.'}
          </p>
          <div className="flex justify-center">
            <Link to="/contact">
              <Button className="bg-white text-purple-600 hover:bg-gray-100 text-lg px-12 py-7 rounded-full shadow-2xl hover:shadow-white/50 transition-all duration-300 hover:scale-105 font-semibold">
                {data.settings?.cta_button1_text || 'Book Appointment'}
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
